import { MessagingTopology } from '@internal/open-catalog';
import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import { ConnectionLineType, Controls, Edge, Node, ReactFlow, useEdgesState, useNodesState } from '@xyflow/react';
import '@xyflow/react/dist/base.css';
import React, { useCallback, useEffect, useState } from 'react';
import { layoutWithELK, MessagingNodeType, parseDraftTopology, useFlowLayoutStyles } from '../flow';
import { InputToolbar } from './toolbar.input';
import { InputSourceToolbar } from './toolbar.input.source';
import { InputTargetToolbar } from './toolbar.input.target';
import { MainToolbar } from './toolbar.main';
import { OutputToolbar } from './toolbar.output';
import { OutputTargetToolbar } from './toolbar.output.target';

function ToolbarPanel(props: {
  topology: MessagingTopology,
  selected: any,
  smartTalkApplicationId?: string,
  onTopologyChange: (newNodeName?: string) => void
}) {

  const { topology, selected, smartTalkApplicationId, onTopologyChange } = props;

  if (!selected) {
    return (<MainToolbar topology={topology} onTopologyChange={onTopologyChange} smartTalkApplicationId={smartTalkApplicationId} />);
  }

  if (selected.data.spec.name === topology.input?.name) {
    return (<InputToolbar data={selected.data as any} topology={topology} onTopologyChange={onTopologyChange} />);
  }

  if (selected.data.spec.name === topology.output?.name) {
    return (<OutputToolbar data={selected.data as any} topology={topology} onTopologyChange={onTopologyChange} />);
  }

  if (selected.data.type === 'exchange') {

    if (selected.data.spec.name.endsWith('-output')) {
      return (<InputSourceToolbar data={selected.data as any} topology={topology} onTopologyChange={onTopologyChange} />);
    }

    if (selected.data.spec.name.endsWith('-input')) {
      return (<OutputTargetToolbar data={selected.data as any} topology={topology} onTopologyChange={onTopologyChange} />);
    }
  }

  return (<InputTargetToolbar data={selected.data as any} topology={topology} onTopologyChange={onTopologyChange} />);
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridContainer: {
      height: '100%'
    },
    flowContainer: {
      width: '100vw',
      height: 'calc(100% + 16px)',
      display: 'flex',
      '& div[class|="makeStyles-floatToolbar"]': {
        right: theme.spacing(3),
        '& div[class|="MuiAccordionDetails"]': {
          maxHeight: '58vh'
        }
      }
    },
    floatToolbar: {
      position: 'absolute',
      width: '25%',
      right: theme.spacing(6),
      zIndex: 10001
    },
  })
);

export function TopologyEdit(props: {
  topology: MessagingTopology,
  focusMode: boolean,
  searchValue: string,
  smartTalkApplicationId?: string
}) {

  const [selected, setSelected] = useState<any>();
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([] as any);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const classes = useStyles();
  const classesFlowLayout = useFlowLayoutStyles();

  const { topology, focusMode, searchValue, smartTalkApplicationId } = props;

  const onPaneClick = useCallback((_: React.MouseEvent) => {

    const animatedEdges = (edges || []).map((e: Edge) => {
      e.animated = false
      return e;
    });

    onEdgesChange(animatedEdges as any);
    setSelected(undefined);

  }, [edges, onEdgesChange, setSelected]);

  const animateEdges = (node: Node, edgesToAnimate?: Edge[]) => {
    return (edgesToAnimate || []).map((e: Edge) => {
      e.selected = false;
      e.animated = e.source === node.id || e.target === node.id;
      return e;
    });
  };
  

  const onNodeClick = useCallback((_: React.MouseEvent, selectedNode: Node) => {

    const selectedNodes = (nodes || [])
      .map((n: Node) => {
        n.selected = (n.id === selectedNode.id);
        return n;
      });

    const animatedEdges = animateEdges(selectedNode, edges);

    onNodesChange(selectedNodes as any);
    onEdgesChange(animatedEdges as any);

    setSelected(selectedNode);

  }, [edges, nodes, onNodesChange, onEdgesChange, setSelected]);

  const onInit = (instace: any) => {
    setReactFlowInstance(instace);
  };

  useEffect(() => {
    setTimeout(() => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView();
      }
    }, 100);
  }, [reactFlowInstance]);

  const updateReactFlow = useCallback(async (newNodeName?:string) => {

    const { nodes: parsedNodes, edges: parsedEdges } = parseDraftTopology(
      topology, focusMode, searchValue
    );

    const layouted = await layoutWithELK(parsedNodes, parsedEdges, {
      rootNode: classesFlowLayout.rootNodeWrapper,
      node: classesFlowLayout.nodeWrapper,
      edge: classesFlowLayout.edgeWrapper
    });

    let selectedNode: Node | undefined;

    setNodes(layouted.nodes.map(node => {
      node.selected = (node.data as any).spec.name === newNodeName;
      if (node.selected) {
        selectedNode = node;
      }
      return node;
    }) as any);

    if(selectedNode) {
      layouted.edges = animateEdges(selectedNode, layouted.edges);
    }
    
    setEdges(layouted.edges as any);
    if (selectedNode){
      setSelected(selectedNode);
    }

  }, [topology, focusMode, searchValue, setNodes, setEdges, classesFlowLayout]);

  useEffect(() => {
    updateReactFlow();
  }, [updateReactFlow])

  return (<Grid container className={classes.gridContainer}>
    <Grid item xs sm={12} className={classes.flowContainer}>
      {nodes && edges && (
        <ReactFlow fitView
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={{ default: MessagingNodeType } as any}
          connectionLineType={ConnectionLineType.SmoothStep}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onInit={onInit}
        >
          <Controls showInteractive={false} />
        </ReactFlow>
      )}
      <div className={classes.floatToolbar}>
        <ToolbarPanel topology={topology} selected={selected as any}
          onTopologyChange={updateReactFlow}
          smartTalkApplicationId={smartTalkApplicationId}
        />
      </div>
    </Grid>
  </Grid>);
}
